import { useState, useEffect, useRef, useMemo } from "react";
import useTeacherNavStore from "../../../stores/useTeacherNavStore";
import { AplPeriodGroup } from "../../../constants/AplPeriodGroup";

const getPeriodGroup = (periodItem) => {
  if (periodItem.period.isActive) return AplPeriodGroup.ONGOING;
  if (periodItem.period.isUpcoming) return AplPeriodGroup.UPCOMING;
  return AplPeriodGroup.ENDED;
};

const sortPeriodsInGroup = (group, periods) =>
  periods.sort((a, b) =>
    group === AplPeriodGroup.ENDED ? b.to - a.to : a.from - b.from,
  );

const getStudentsInView = (students, level, objId) =>
  students.filter(({ student }) =>
    level === "Student"
      ? student.id === objId
      : level === "Class"
        ? student.classId === objId
        : true,
  );

export default function useAPLPeriodData(periodsData, level, objId) {
  const [period, setPeriod] = useState(null);
  const [activePeriodId, setActivePeriodId] = useState(null);
  const [periodsToSelect, setPeriodsToSelect] = useState([]);
  const [currentPeriods, setCurrentPeriods] = useState(null);
  const periodIdRef = useRef(null);
  const selectedAplPeriodId = useTeacherNavStore((state) => state.selectedAplPeriodId);
  const selectedAplTab = useTeacherNavStore((state) => state.selectedAplTab);

  // Effect for setting period based on navReducer selection
  useEffect(() => {
    if (
      !periodsData.isPending &&
      periodsData.data &&
      periodsData.data.some(
        (prd) => prd.period.id === selectedAplPeriodId
      )
    ) {
      const tempPeriod = periodsData.data.find(
        (prd) => prd.period.id === selectedAplPeriodId
      );

      // Deep clone to avoid mutation issues
      const clonedPeriod = structuredClone(tempPeriod);

      clonedPeriod.period.students = getStudentsInView(
        clonedPeriod.period.students,
        level,
        objId,
      );

      clonedPeriod.period.students.sort((a, b) => {
        const nameA = a.student.name.toUpperCase();
        const nameB = b.student.name.toUpperCase();
        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
        return 0;
      });

      setPeriod(clonedPeriod);
      setActivePeriodId(selectedAplPeriodId);
    }
  }, [
    selectedAplPeriodId,
    periodsData.isPending,
    periodsData.data,
    objId,
    level,
  ]);

  // Effect for setting currentPeriods
  useEffect(() => {
    if (!periodsData.isPending) {
      setCurrentPeriods(periodsData.data);
    }
  }, [periodsData.data, periodsData.isPending]);

  // Keep periodIdRef in sync with current period
  useEffect(() => {
    periodIdRef.current = period?.period?.id ?? null;
  }, [period]);

  // Effect for setting periodsToSelect dropdown options
  useEffect(() => {
    if (!periodsData.isPending) {
      const tempSelectablePeriods = [];
      if (periodsData.data) {
        periodsData.data.forEach((periodItem) => {
          const dateFrom = new Date(periodItem.period.from);
          const dateTo = new Date(periodItem.period.to);
          const p = {
            value: periodItem.period.id,
            label:
              periodItem.period.name +
              (periodItem.period.id !== 0
                ? " - " +
                  dateFrom.toLocaleDateString("sv") +
                  " till " +
                  dateTo.toLocaleDateString("sv")
                : ""),
            group: getPeriodGroup(periodItem),
            from: dateFrom,
            to: dateTo,
            students: getStudentsInView(
              periodItem.period.students,
              level,
              objId,
            ).map(({ student }) => ({
              id: student.id,
              name: student.name,
              className: student.class?.name,
            })),
          };
          tempSelectablePeriods.push(p);
        });

        setPeriodsToSelect(tempSelectablePeriods);
      }
      if (periodIdRef.current !== null) {
        if (
          !tempSelectablePeriods.find((p) => p.value === periodIdRef.current)
        ) {
          setPeriod(null);
        }
      }
    }
  }, [periodsData.data, periodsData.isPending, level, objId]);

  const periodGroups = useMemo(
    () =>
      Object.values(AplPeriodGroup).map((group) => ({
        group,
        periods: sortPeriodsInGroup(
          group,
          periodsToSelect.filter(
            (periodOption) => periodOption.group === group,
          ),
        ),
      })),
    [periodsToSelect],
  );

  return {
    period,
    setPeriod,
    activePeriodId,
    periodsToSelect,
    periodGroups,
    currentPeriods,
    setCurrentPeriods,
    selectedAplTab: selectedAplTab,
  };
}
